// Detail-page document picker. Drives the three-step flow per file:
//   1. POST /presentations/{id}/documents/sign-upload → presigned URL
//   2. PUT the bytes directly to the presigned URL
//   3. POST /presentations/{id}/documents → commit (returns row HTML)
// Successful add appends the server-rendered row in-place; remove
// drops the row in-place. No reload — keeps scroll + iframe playback
// intact.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-presentation-documents]";
const FALLBACK_ATTACH_ERROR =
  "Could not attach the document. Please try again.";
const FALLBACK_REMOVE_ERROR =
  "Could not remove the document. Please try again.";

export function initPresentationDocuments(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(el) {
  if (!firstBind(el)) return;

  const presentationID = el.dataset.presentationId;
  if (!presentationID) return;

  const input = el.querySelector("[data-presentation-documents-input]");
  const list = el.querySelector("[data-presentation-documents-list]");
  const empty = el.querySelector("[data-presentation-documents-empty]");
  const status = el.querySelector("[data-presentation-documents-status]");
  const errorEl = el.querySelector("[data-presentation-documents-error]");

  const signURL = `/presentations/${presentationID}/documents/sign-upload`;
  const commitURL = `/presentations/${presentationID}/documents`;

  if (input) {
    input.addEventListener("change", async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;

      hideError(errorEl);
      input.disabled = true;

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setStatus(
            status,
            `Uploading ${file.name} (${i + 1} of ${files.length})…`,
          );
          const rowHTML = await uploadOne(file, signURL, commitURL);
          if (list) list.insertAdjacentHTML("beforeend", rowHTML);
          toggleEmpty(empty, list);
        }
        hideStatus(status);
      } catch (err) {
        showError(errorEl, errorMessage(err, FALLBACK_ATTACH_ERROR));
        hideStatus(status);
      } finally {
        input.disabled = false;
        input.value = "";
      }
    });
  }

  el.addEventListener("click", async (event) => {
    const btn = event.target.closest("[data-presentation-document-remove]");
    if (!btn || !el.contains(btn)) return;
    const docID = btn.dataset.presentationDocumentRemove;
    if (!docID) return;

    btn.disabled = true;
    hideError(errorEl);
    try {
      const resp = await fetch(`${commitURL}/${docID}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!resp.ok) throw await asUserError(resp);
      const row = btn.closest(`[data-presentation-document-id="${docID}"]`);
      if (row) row.remove();
      toggleEmpty(empty, list);
    } catch (err) {
      btn.disabled = false;
      showError(errorEl, errorMessage(err, FALLBACK_REMOVE_ERROR));
    }
  });
}

async function uploadOne(file, signURL, commitURL) {
  const signParams = new URLSearchParams();
  signParams.append("filename", file.name);
  signParams.append("content_type", file.type || "application/octet-stream");
  signParams.append("file_size_bytes", String(file.size));

  const signResp = await fetch(signURL, {
    method: "POST",
    body: signParams,
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!signResp.ok) throw await asUserError(signResp);
  const signed = await signResp.json();
  if (!signed.upload_url || !signed.key) {
    throw new Error(FALLBACK_ATTACH_ERROR);
  }

  // upload_headers are the signed metadata headers from the sign step;
  // S3 rejects the PUT signature unless they're echoed back verbatim.
  const putResp = await fetch(signed.upload_url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(signed.upload_headers || {}),
    },
  });
  if (!putResp.ok) throw new Error(FALLBACK_ATTACH_ERROR);

  const commitParams = new URLSearchParams();
  commitParams.append("key", signed.key);
  commitParams.append("title", titleFromFilename(file.name));

  const commitResp = await fetch(commitURL, {
    method: "POST",
    body: commitParams,
    headers: { Accept: "text/html" },
    credentials: "same-origin",
  });
  if (!commitResp.ok) throw await asUserError(commitResp);

  return commitResp.text();
}

function toggleEmpty(empty, list) {
  if (!empty || !list) return;
  const hasRows =
    list.querySelectorAll("[data-presentation-document-id]").length > 0;
  empty.classList.toggle("hidden", hasRows);
}

function titleFromFilename(name) {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name).slice(0, 200);
}

function setStatus(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

function hideStatus(el) {
  if (el) el.classList.add("hidden");
}

function showError(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

function hideError(el) {
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}

// Server already returns user-friendly strings on 4xx; surface them
// verbatim. Network or 5xx falls through to the caller-supplied
// fallback so the user sees something actionable.
async function asUserError(resp) {
  if (resp.status >= 400 && resp.status < 500) {
    try {
      const body = await resp.json();
      if (body && body.error) return new Error(body.error);
    } catch (_) {
      // fall through
    }
  }
  return new Error(`upstream ${resp.status}`);
}

function errorMessage(err, fallback) {
  const raw = err && err.message ? String(err.message) : "";
  if (raw && !/^upstream /.test(raw) && raw !== fallback) return raw;
  return fallback;
}
